/**
 * Tiled-homography compositor with alpha-mask occlusion.
 *
 * Two upgrades over /visualizer/homographyComposite.js:
 *
 *   1. TILING — instead of warping ONE panel image to fill the whole wall
 *      quadrilateral, we split the quad into N side-by-side strips and
 *      warp the panel into each strip.  Matches how real wall panels are
 *      installed (multiple instances side by side) and lets the user
 *      adjust tile count via a slider when the auto-default is off.
 *
 *      Each strip is itself a sub-quadrilateral whose top edge is a slice
 *      of the wall's top edge and bottom edge is a slice of the wall's
 *      bottom edge.  Linear interpolation along each edge — not perfectly
 *      perspective-correct on extreme trapezoidal walls, but close enough
 *      that the user can fix any drift with the slider.
 *
 *   2. OCCLUSION MASK — an optional alpha mask painted by the user marks
 *      furniture/objects that should NOT be covered by the panel.  After
 *      drawing the warped panels, we composite the mask via
 *      `globalCompositeOperation = 'destination-out'` to "punch out" the
 *      panel pixels under the mask, revealing the original room photo.
 *
 * Math reference: solving a 3×3 homography from 4 (src→dst) point pairs is
 * an 8-equation linear system; once H is known, applying it to (u,v) gives
 * the destination pixel via [x,y,w] = H·[u,v,1] then x/=w, y/=w.  We
 * approximate the perspective texture warp by subdividing each tile into a
 * 32×32 affine triangle grid (same trick as the simpler compositor).
 */

/** Solve a 3×3 homography mapping the unit square (0..1)² to dst[4]. */
export function homographyFromUnitSquare(dst) {
  if (dst.length !== 4) throw new Error("homography: need 4 dst points");
  const sx = [0, 1, 1, 0];
  const sy = [0, 0, 1, 1];
  const A = [];
  const b = [];
  for (let i = 0; i < 4; i++) {
    const u = sx[i], v = sy[i], x = dst[i].x, y = dst[i].y;
    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x]);  b.push(x);
    A.push([0, 0, 0, u, v, 1, -u * y, -v * y]);  b.push(y);
  }
  const h = solve8x8(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

function applyH(H, u, v) {
  const x = H[0] * u + H[1] * v + H[2];
  const y = H[3] * u + H[4] * v + H[5];
  const w = H[6] * u + H[7] * v + H[8];
  return { x: x / w, y: y / w };
}

function solve8x8(A, b) {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i++) {
    let max = Math.abs(M[i][i]); let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(M[k][i]) > max) { max = Math.abs(M[k][i]); maxRow = k; }
    }
    [M[i], M[maxRow]] = [M[maxRow], M[i]];
    if (Math.abs(M[i][i]) < 1e-12) throw new Error("Homography: degenerate quad");
    for (let k = i + 1; k < n; k++) {
      const f = M[k][i] / M[i][i];
      for (let j = i; j <= n; j++) M[k][j] -= f * M[i][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
}

/** Linear interpolation between two points. */
function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/**
 * Per-texture mipmap pyramid.  Stores progressively-halved canvases so we
 * never downsample a 1200×2800 panel directly to ~300×700 in a single
 * bilinear pass (which would alias the high-frequency detail into moiré).
 * Chained halvings encode proper Gaussian-equivalent filtering, so the
 * level we pick is already close to the destination size and the final
 * `drawImage` warp operates near 1:1.
 *
 * Built lazily on first request per texture, kept in a WeakMap so garbage
 * collection of the texture image releases its pyramid automatically.
 */
const mipmapCache = new WeakMap();

/** Helper: get an HTMLImageElement / HTMLCanvasElement's pixel width. */
function pxW(im) { return im.naturalWidth || im.width || 0; }
function pxH(im) { return im.naturalHeight || im.height || 0; }

/**
 * Return a mipmap level whose dimensions are still ≥ (destW, destH) but as
 * close as possible — i.e., the smallest version of `tex` that the warp
 * can sample without losing real detail.  If the texture is already
 * within 2× of the destination size, we return it as-is (bilinear is
 * correct in that range).
 *
 * Each new level is generated by halving the previous level via
 * `drawImage(prev, ..., halfW, halfH)` with `imageSmoothingQuality:
 * 'high'` — chained halvings approximate a Gaussian box filter, which
 * is the textbook anti-aliased downsample.
 */
function getMipmapForScale(tex, destW, destH) {
  const tw = pxW(tex), th = pxH(tex);
  if (!tw || !th || destW <= 0 || destH <= 0) return tex;
  // Already close enough — bilinear handles ≤2× downsample correctly.
  if (tw <= destW * 2 && th <= destH * 2) return tex;

  let pyramid = mipmapCache.get(tex);
  if (!pyramid) { pyramid = []; mipmapCache.set(tex, pyramid); }

  // Build halvings until the current level is within 2× of the target.
  let cur = tex, curW = tw, curH = th;
  while (curW > destW * 2 && curH > destH * 2) {
    const halfW = Math.max(1, Math.floor(curW / 2));
    const halfH = Math.max(1, Math.floor(curH / 2));
    const cached = pyramid.find((l) => l.w === halfW && l.h === halfH);
    if (cached) {
      cur = cached.canvas;
    } else {
      const c = document.createElement("canvas");
      c.width = halfW; c.height = halfH;
      const cctx = c.getContext("2d");
      cctx.imageSmoothingEnabled = true;
      cctx.imageSmoothingQuality = "high";
      cctx.drawImage(cur, 0, 0, halfW, halfH);
      pyramid.push({ w: halfW, h: halfH, canvas: c });
      cur = c;
    }
    curW = halfW; curH = halfH;
  }
  return cur;
}

/**
 * Compute the auto-default tile count for B1 — match the panel's natural
 * aspect to the wall's pixel aspect so the panel renders at proportions
 * close to its real-world look.
 *
 *   tile_count ≈ wall_aspect / panel_aspect
 *   where aspect = width / height
 *
 * Returns at least 1, capped at 12 so a misshapen quadrilateral can't
 * produce 60 absurdly thin tiles.
 */
export function suggestTileCount(corners, panelW, panelH) {
  if (!corners || corners.length !== 4 || !panelW || !panelH) return 3;
  // Approximate wall width = average of top + bottom edge lengths.
  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const wallW = (dist(corners[0], corners[1]) + dist(corners[3], corners[2])) / 2;
  const wallH = (dist(corners[0], corners[3]) + dist(corners[1], corners[2])) / 2;
  if (wallH < 1 || wallW < 1) return 3;
  const wallAspect = wallW / wallH;
  const panelAspect = panelW / panelH;
  return Math.max(1, Math.min(12, Math.round(wallAspect / panelAspect)));
}

/**
 * Render the room photo + a single tiled panel wall + furniture mask cutout.
 *
 * @param {HTMLCanvasElement} canvas        Where to draw.
 * @param {HTMLImageElement}  baseImage     The room photo.
 * @param {Object}            panelLayer
 * @param {HTMLImageElement}  panelLayer.texture   Loaded panel image.
 * @param {Array<{x,y}>}      panelLayer.corners   Wall's 4 corners (TL, TR, BR, BL).
 * @param {number}            panelLayer.tileCount How many panels to tile across.
 * @param {HTMLCanvasElement|null} maskCanvas  Optional alpha mask — opaque
 *        pixels mark furniture and get punched out of the panel composite.
 *
 * Mutates the canvas in place.  Caller sizes the canvas beforehand.
 */
export function renderTiledComposite(canvas, baseImage, panelLayer, maskCanvas) {
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);

  if (panelLayer && panelLayer.texture && panelLayer.corners?.length === 4) {
    const { texture, corners, tileCount } = panelLayer;
    const tiles = Math.max(1, Math.floor(tileCount || 1));
    // For each tile, build its sub-quadrilateral by slicing the top and
    // bottom edges of the wall at i/N and (i+1)/N.
    for (let i = 0; i < tiles; i++) {
      const t0 = i / tiles, t1 = (i + 1) / tiles;
      const subCorners = [
        lerp(corners[0], corners[1], t0),  // TL
        lerp(corners[0], corners[1], t1),  // TR
        lerp(corners[3], corners[2], t1),  // BR
        lerp(corners[3], corners[2], t0),  // BL
      ];
      drawWarpedTexture(ctx, texture, subCorners);
    }
  }

  // Punch out the mask — destination-out keeps existing pixels where the
  // mask is transparent, removes them where the mask is opaque.  We then
  // re-draw the room photo through the holes so the original photo shows.
  if (maskCanvas) {
    // Step 1: punch out the panel under the mask (destination-out) so a
    // translucent "hole" appears.
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Step 2: re-fill the holes with the original room photo so furniture
    // shows in the foreground.  destination-over only paints pixels that
    // are currently transparent — exactly the holes we just made.
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.drawImage(baseImage, 0, 0, canvas.width, canvas.height);
    ctx.restore();
  }
}

/**
 * Draw the texture warped to fill the given quadrilateral (TL, TR, BR, BL).
 *
 * Three-part anti-seam strategy (replaces the older "clip a triangle per
 * grid cell" approach which left a faint mesh of white lines on solid
 * panels):
 *
 * 1. **Render to an opaque-backed offscreen canvas first.**  Created with
 *    `{ alpha: false }` so the backing store is fully opaque from the
 *    start.  Anti-aliased clip-edge pixels then blend against the opaque
 *    average-texture-color background instead of transparent, so the
 *    photo behind the wall cannot bleed through interior seams.  The
 *    offscreen is then blitted to the main canvas with a SINGLE outer
 *    clip = the wall quad, so the only AA edge exposed to the photo is
 *    the wall outline itself.
 *
 * 2. **One quad-clip per grid cell, not two triangle-clips.**  A 4-vertex
 *    quad has no diagonal hypotenuse, so the most visible (non-axis-
 *    aligned) seam disappears entirely.  The cell still uses a single
 *    affine transform — derived from 3 of the 4 corners; the 4th is
 *    approximated, but at our grid density (N×N below) the in-cell
 *    bilinear error is sub-pixel.
 *
 * 3. **Generous clip-path inflation (~1.5 px outward from cell
 *    centroid).**  Canvas's AA fringe at clip edges is roughly 1 px wide
 *    on each side; 0.5 px overlap (the previous setting) covered only
 *    half of it, leaving faint lines.  1.5 px gives a 3 px overlap
 *    strip, well clear of the AA fringe in either direction so adjacent
 *    cells fully cover each other's anti-aliased edges under default
 *    `source-over` compositing.
 *
 * Combined effect: the visible "graph paper" pattern of triangle seams
 * becomes imperceptible at typical viewing distances.
 */
function drawWarpedTexture(ctx, tex, dst) {
  const H = homographyFromUnitSquare(dst);
  if (!pxW(tex) || !pxH(tex)) return;

  // Approximate the destination quad's pixel dimensions so we can pick a
  // mipmap level whose source resolution is close to its target size.
  // Drawing from a too-large source through one bilinear pass is what
  // creates the moiré rings on detailed textures (you watched it happen
  // on the green-swirl panel).  A pre-filtered mipmap fixes it.
  const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const destW = (dist(dst[0], dst[1]) + dist(dst[3], dst[2])) / 2;
  const destH = (dist(dst[0], dst[3]) + dist(dst[1], dst[2])) / 2;
  const sourceTex = getMipmapForScale(tex, destW, destH);
  const tw = pxW(sourceTex), th = pxH(sourceTex);
  if (!tw || !th) return;

  // Bounding box of the destination quad in main-canvas pixels, padded
  // 4 px so the inflated outermost cells don't get cropped at the edge.
  const PAD = 4;
  const minX = Math.floor(Math.min(dst[0].x, dst[1].x, dst[2].x, dst[3].x) - PAD);
  const maxX = Math.ceil(Math.max(dst[0].x, dst[1].x, dst[2].x, dst[3].x) + PAD);
  const minY = Math.floor(Math.min(dst[0].y, dst[1].y, dst[2].y, dst[3].y) - PAD);
  const maxY = Math.ceil(Math.max(dst[0].y, dst[1].y, dst[2].y, dst[3].y) + PAD);
  const offW = Math.max(1, maxX - minX);
  const offH = Math.max(1, maxY - minY);

  const off = document.createElement("canvas");
  off.width = offW;
  off.height = offH;
  // Opaque backing so AA fringes blend against an opaque pixel rather
  // than transparent — eliminates "photo-bleeds-through-the-seam" entirely.
  const offCtx = off.getContext("2d", { alpha: false });
  // High-quality bilinear for the per-cell warp draws too — even though
  // the mipmap step has done the heavy lifting, this guarantees the
  // remaining ≤2× resample is filtered properly.
  offCtx.imageSmoothingEnabled = true;
  offCtx.imageSmoothingQuality = "high";
  // Pre-fill with a low-saturation neutral so any rare uncovered fringe
  // pixel reads as a soft tone rather than glaring white.  Texture
  // overdraw immediately covers all but sub-pixel slivers.
  offCtx.fillStyle = "#888";
  offCtx.fillRect(0, 0, offW, offH);
  // Translate the offscreen so destination-canvas coordinates can be
  // used directly (origin of dst quad maps relative to offscreen).
  offCtx.translate(-minX, -minY);

  const N = 48;  // bumped from 32 — more cells = smaller per-cell affine
                  // approximation error at the cost of ~2.25× more draws.
  const grid = new Array((N + 1) * (N + 1));
  for (let j = 0; j <= N; j++) {
    for (let i = 0; i <= N; i++) {
      grid[j * (N + 1) + i] = applyH(H, i / N, j / N);
    }
  }
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      const p00 = grid[j * (N + 1) + i];        // TL
      const p10 = grid[j * (N + 1) + (i + 1)];  // TR
      const p11 = grid[(j + 1) * (N + 1) + (i + 1)]; // BR
      const p01 = grid[(j + 1) * (N + 1) + i];       // BL
      const u0 = (i / N) * tw, u1 = ((i + 1) / N) * tw;
      const v0 = (j / N) * th, v1 = ((j + 1) / N) * th;
      // Single quad clip + single affine per cell (TL, TR, BL → 3-point
      // affine; BR is implicit, sub-pixel error inside the cell at N=48).
      // `sourceTex` is the mipmap-selected level — its naturalWidth/height
      // match the (tw, th) above used to compute u0/u1/v0/v1.
      drawTextureCell(offCtx, sourceTex, u0, v0, u1, v0, u0, v1, p00, p10, p11, p01);
    }
  }

  // Single outer clip = the wall quad, inflated outward by ~1 px from
  // its centroid so adjacent panel-tiles overlap along their shared
  // edge.  Without inflation, the AA fringes of two adjacent tiles'
  // clip paths sum to less than 1.0 and leave a thin photo-bleed
  // stripe — the visible "white seam between panels" symptom.
  // With overlap, the second tile painted covers the first's AA
  // fringe under default source-over compositing, killing the seam.
  //
  // Trade-off: outermost wall-edge tiles also inflate ~1 px past the
  // wall boundary, so the panel paints a single pixel beyond the wall
  // corners on the outside.  Visually trivial against the photo
  // background and well worth eliminating the seam.  The inflation
  // (1 px) is less than CELL_INFLATE_PX (1.5 px), so no offscreen-
  // gray pixels are exposed.
  const OUTER_INFLATE_PX = 1.0;
  const inflatedDst = inflateOutwards(dst, OUTER_INFLATE_PX);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(inflatedDst[0].x, inflatedDst[0].y);
  ctx.lineTo(inflatedDst[1].x, inflatedDst[1].y);
  ctx.lineTo(inflatedDst[2].x, inflatedDst[2].y);
  ctx.lineTo(inflatedDst[3].x, inflatedDst[3].y);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(off, minX, minY);
  ctx.restore();
}

/**
 * Pixels to inflate each cell's clip path outward from its centroid.
 * Adjacent cells share an edge; pushing each cell's vertices 1.5 px
 * away from its own centroid means the shared edge ends up INSIDE both
 * cells' expanded clips with a ~3 px overlap strip — wider than the
 * canvas anti-aliased clip-edge fringe (~1 px), so adjacent cells'
 * fully-opaque interiors completely cover each other's AA fringes
 * under default `source-over` compositing.
 *
 * The previous setting (0.5 px) only covered half the AA fringe and
 * left the faint mesh of grid lines visible on solid-colored panels.
 * 1.5 px is the smallest value that reliably eliminates them in
 * practice without making adjacent-cell affine extrapolations clash
 * visibly inside the overlap strip.
 */
const CELL_INFLATE_PX = 1.5;

/** Push each vertex of `verts` outward from their centroid by `px`. */
function inflateOutwards(verts, px) {
  let cx = 0, cy = 0;
  for (const v of verts) { cx += v.x; cy += v.y; }
  cx /= verts.length; cy /= verts.length;
  return verts.map((p) => {
    const dx = p.x - cx, dy = p.y - cy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return { x: p.x, y: p.y };
    return { x: p.x + (dx / len) * px, y: p.y + (dy / len) * px };
  });
}

/**
 * Draw one grid cell as a single clipped quadrilateral with a single
 * affine transform.  Affine is fitted to TL/TR/BL exactly; BR is
 * implicit (no affine matches all 4 vertices of a non-parallelogram).
 * Bilinear error inside the cell at N=48 is well under 1 px on typical
 * walls, so the implicit-BR slack is not visible.
 *
 * Clip path is the cell's full quadrilateral (TL→TR→BR→BL), inflated
 * outward by CELL_INFLATE_PX from the centroid so adjacent cells'
 * clips overlap and cover each other's AA fringe.
 */
function drawTextureCell(ctx, tex, sx1, sy1, sx2, sy2, sx3, sy3, dTL, dTR, dBR, dBL) {
  ctx.save();
  const [eTL, eTR, eBR, eBL] = inflateOutwards([dTL, dTR, dBR, dBL], CELL_INFLATE_PX);
  ctx.beginPath();
  ctx.moveTo(eTL.x, eTL.y);
  ctx.lineTo(eTR.x, eTR.y);
  ctx.lineTo(eBR.x, eBR.y);
  ctx.lineTo(eBL.x, eBL.y);
  ctx.closePath();
  ctx.clip();
  // Affine from 3 source/dest correspondences (TL, TR, BL).  BR isn't
  // used here — within a single small cell the affine extrapolation to
  // BR matches the homography to sub-pixel accuracy at N=48.
  const x1 = sx1, y1 = sy1, x2 = sx2, y2 = sy2, x3 = sx3, y3 = sy3;
  const u1 = dTL.x, v1 = dTL.y, u2 = dTR.x, v2 = dTR.y, u3 = dBL.x, v3 = dBL.y;
  const det = x1 * (y2 - y3) - y1 * (x2 - x3) + (x2 * y3 - x3 * y2);
  if (Math.abs(det) < 1e-12) { ctx.restore(); return; }
  const a = (u1 * (y2 - y3) - y1 * (u2 - u3) + (u2 * y3 - u3 * y2)) / det;
  const b = (v1 * (y2 - y3) - y1 * (v2 - v3) + (v2 * y3 - v3 * y2)) / det;
  const c = (x1 * (u2 - u3) - u1 * (x2 - x3) + (x2 * u3 - x3 * u2)) / det;
  const d = (x1 * (v2 - v3) - v1 * (x2 - x3) + (x2 * v3 - x3 * v2)) / det;
  const e = (x1 * (y2 * u3 - y3 * u2) - y1 * (x2 * u3 - x3 * u2) + u1 * (x2 * y3 - x3 * y2)) / det;
  const f = (x1 * (y2 * v3 - y3 * v2) - y1 * (x2 * v3 - x3 * v2) + v1 * (x2 * y3 - x3 * y2)) / det;
  ctx.transform(a, b, c, d, e, f);
  ctx.drawImage(tex, 0, 0);
  ctx.restore();
}
